// WHAT: Hook for managing Supabase tasks with CRUD operations
// WHY:  Provides interface for fetching, creating, updating, and deleting tasks from Supabase
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: getSupabaseClient from shared services
// RISKS:
//   - NOTE: Error handling may not be user-friendly
//   - NOTE: Task updates may not trigger re-fetch - state may be stale
//   - NOTE: No optimistic updates - UI may lag

import { useEffect, useState, useCallback } from "react";

import { getSupabaseClient } from "@/shared/services";

/**
 * Supabase task interface
 */
export interface SupabaseTask {
  /** Aspect name */
  aspect: null | string;
  /** Task deadline */
  deadline: null | string;
  /** Task effort */
  effort: null | string;
  /** Estimated time in hours */
  estimated_time: null | number;
  /** Task identifier */
  id: string;
  /** Task impact */
  impact: null | string;
  /** Task priority */
  priority: null | string;
  /** Task progress (0-100) */
  progress: null | number;
  /** Task score */
  score: null | number;
  /** Task status */
  status: null | string;
  /** Task name */
  task: string;
  /** Workspace name */
  workspace: null | string;
}

/**
 * WHAT:
 *   Hook that provides CRUD operations for Supabase tasks with local state
 *   management and automatic fetching on mount.
 *
 * WHY:
 *   Centralizes task management logic and provides consistent interface for
 *   task operations across the application.
 *
 * BEHAVIOR:
 *   - Fetches tasks on mount
 *   - Provides createTask function
 *   - Provides updateTask function
 *   - Provides deleteTask function
 *   - Provides reload function
 *   - Manages loading state
 *
 * RISKS:
 *   - NOTE: Error handling may not be user-friendly
 *   - NOTE: Task updates may not trigger re-fetch - state may be stale
 *   - NOTE: No optimistic updates - UI may lag
 */
interface UseSupabaseTasksReturn {
  createTask: (task: Omit<SupabaseTask, "id">) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  loading: boolean;
  reload: () => Promise<void>;
  tasks: SupabaseTask[];
  updateTask: (id: string, updates: Partial<SupabaseTask>) => Promise<void>;
}

export const useSupabaseTasks = (): UseSupabaseTasksReturn => {
  const [tasks, setTasks] = useState<SupabaseTask[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase.from("tasks").select("*");

    if (error) {
      // NOTE: Error loading tasks from Supabase
      setTasks([]);
    } else {
      setTasks((data as SupabaseTask[]) ?? []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const createTask = async (task: Omit<SupabaseTask, "id">) => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase.from("tasks").insert(task).select();

    if (error) {
      throw new Error(error.message);
    }
    if (data.length > 0) {
      const firstTask = data[0] as SupabaseTask;
      setTasks((previous) => [...previous, firstTask]);
    }
  };

  const updateTask = async (id: string, updates: Partial<SupabaseTask>) => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase.from("tasks").update(updates).eq("id", id).select();

    if (error) {
      throw new Error(error.message);
    }
    if (data.length > 0) {
      const firstTask = data[0] as SupabaseTask;
      setTasks((previous) => previous.map((t) => (t.id === id ? firstTask : t)));
    }
  };

  const deleteTask = async (id: string) => {
    const supabase = await getSupabaseClient();
    const { error } = await supabase.from("tasks").delete().eq("id", id);
    if (error) {
      throw new Error(error.message);
    }
    setTasks((previous) => previous.filter((t) => t.id !== id));
  };

  return {
    createTask,
    deleteTask,
    loading,
    reload: fetchTasks,
    tasks,
    updateTask,
  };
};
